/**
 * Graduated Discount Utility
 * Handles discount calculations for retainer billing with yearly progression
 *
 * Discount Schedule:
 * Year 1: 80% off → client pays 20% of base retainer
 * Year 2: 75% off → client pays 25% of base retainer
 * Year 3: 70% off → client pays 30% of base retainer
 * Year 4: 65% off → client pays 35% of base retainer
 * Year 5+: 60% off → client pays 40% of base retainer
 */

/**
 * Default graduated discount schedule
 * Each entry: { year, discountPercent }
 */
export const DEFAULT_GRADUATED_DISCOUNT = [
  { year: 1, discountPercent: 80 },
  { year: 2, discountPercent: 75 },
  { year: 3, discountPercent: 70 },
  { year: 4, discountPercent: 65 },
  { year: 5, discountPercent: 60 }, // This applies for year 5 and beyond
];

/**
 * Calculate the current year number based on start date
 * @param {Date|string} startDate - The start date of the client relationship
 * @returns {number} Current year number (1-indexed)
 */
export const calculateCurrentYear = (startDate) => {
  if (!startDate) return 1;

  const start = startDate instanceof Date ? startDate : new Date(startDate);
  if (isNaN(start.getTime())) return 1;

  const now = new Date();
  const diffMs = now - start;
  const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);

  // Year 1 = 0-1 years, Year 2 = 1-2 years, etc.
  return Math.floor(diffYears) + 1;
};

/**
 * Calculate graduated discount based on client's discount settings
 * @param {Object} graduatedDiscount - Client's graduated discount settings
 * @param {Date} graduatedDiscount.startDate - When the graduated discount started
 * @param {Array} graduatedDiscount.schedule - Array of { year, discountPercent }
 * @returns {Object} { discountPercent, currentYear, schedule }
 */
export const calculateGraduatedDiscount = (graduatedDiscount) => {
  if (!graduatedDiscount || !graduatedDiscount.enabled) {
    return {
      discountPercent: 0,
      currentYear: null,
      schedule: null,
      enabled: false,
    };
  }

  const startDate = graduatedDiscount.startDate;
  const schedule = graduatedDiscount.schedule || DEFAULT_GRADUATED_DISCOUNT;
  const currentYear = calculateCurrentYear(startDate);

  // Find the applicable discount for current year
  // Sort by year descending to find the highest year that's <= currentYear
  const sortedSchedule = [...schedule].sort((a, b) => b.year - a.year);
  const applicableEntry = sortedSchedule.find((entry) => entry.year <= currentYear);

  const discountPercent = applicableEntry ? applicableEntry.discountPercent : 0;

  return {
    discountPercent,
    currentYear,
    schedule,
    enabled: true,
    startDate,
  };
};

/**
 * Calculate the final retainer amount after applying graduated discount
 * @param {number} baseRetainer - Base monthly retainer amount
 * @param {number} discountPercent - Discount percentage (e.g., 80 for 80% off)
 * @returns {number} Final amount after discount
 */
export const calculateRetainerAmount = (baseRetainer, discountPercent) => {
  if (!baseRetainer || baseRetainer <= 0) return 0;
  if (!discountPercent || discountPercent <= 0) return baseRetainer;

  const discountMultiplier = (100 - discountPercent) / 100;
  return Math.round(baseRetainer * discountMultiplier * 100) / 100; // Round to 2 decimal places
};

/**
 * Get billing period string in YYYY-MM format
 * @param {Date} date - Date to get billing period for (defaults to current date)
 * @returns {string} Billing period in YYYY-MM format
 */
export const getBillingPeriod = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Format billing period for display
 * @param {string} period - Billing period in YYYY-MM format
 * @returns {string} Formatted billing period (e.g., "January 2026")
 */
export const formatBillingPeriod = (period) => {
  if (!period) return '';

  const [year, month] = period.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);

  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Generate line item description with discount info
 * @param {string} billingPeriod - Billing period string
 * @param {Object} discountInfo - Discount information from calculateGraduatedDiscount
 * @returns {string} Line item description
 */
export const generateLineItemDescription = (billingPeriod, discountInfo) => {
  const periodDisplay = formatBillingPeriod(billingPeriod);
  let description = `Monthly Retainer - ${periodDisplay}`;

  if (discountInfo && discountInfo.enabled && discountInfo.discountPercent > 0) {
    description += ` (Year ${discountInfo.currentYear}: ${discountInfo.discountPercent}% discount)`;
  }

  return description;
};

/**
 * Calculate discount savings
 * @param {number} baseRetainer - Base monthly retainer amount
 * @param {number} discountPercent - Discount percentage
 * @returns {Object} { originalAmount, discountAmount, finalAmount, savings }
 */
export const calculateDiscountSavings = (baseRetainer, discountPercent) => {
  const originalAmount = baseRetainer || 0;
  const finalAmount = calculateRetainerAmount(baseRetainer, discountPercent);
  const discountAmount = originalAmount - finalAmount;

  return {
    originalAmount,
    discountAmount,
    finalAmount,
    discountPercent: discountPercent || 0,
    savings: discountAmount,
  };
};

/**
 * Get discount schedule display info
 * @param {Array} schedule - Array of { year, discountPercent }
 * @param {number} currentYear - Current year number
 * @returns {Array} Array with display info for each year
 */
export const getScheduleDisplayInfo = (schedule, currentYear) => {
  const sortedSchedule = [...(schedule || DEFAULT_GRADUATED_DISCOUNT)].sort((a, b) => a.year - b.year);

  return sortedSchedule.map((entry) => ({
    ...entry,
    isCurrent: entry.year === currentYear,
    payPercent: 100 - entry.discountPercent,
    label:
      entry.year === sortedSchedule[sortedSchedule.length - 1].year
        ? `Year ${entry.year}+`
        : `Year ${entry.year}`,
  }));
};
